import { handleApiError, handleApiResponse } from "@/methods/api";
import { renumberQuestionSerialsAcrossSections, toActivitySectionInput } from "@/methods/activity";
import ApiVerified from "@/services/apiVerified";
import { PdfReportDisplay } from "@/types/affiliation";
import { ApiResponse, InputOption } from "@/types/global";
import {
  TestPaperActivityInputs,
  TestPaperTemplateInputs,
  TestPaperTemplateInputsWire,
  TestSeriesBatch,
  TestSeriesDisplay,
  TestSeriesInputs,
  TestSeriesUpdate,
  UpdateTestSeriesSetInputs,
} from "@/types/testSeries";

export default class TestSeriesApi extends ApiVerified {
  async createTestSeries(testSeriesInputs: TestSeriesInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `test-series/create-test-series`,
        testSeriesInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateTestSeries(testSeriesInputs: TestSeriesUpdate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `test-series/update-test-series`,
        testSeriesInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllTestSeries(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<InputOption>>(
        `test-series/get-all-test-series`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTestSeriesDetails(mongoId: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<TestSeriesDisplay>(
        `test-series/get-test-series-details`,
        { params: { mongoId: mongoId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async UpdateCoursesInTestSeries(mongoId: string, courseIds: Array<number>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `test-series/update-courses-in-test-series`,
        { MongoId: mongoId, CourseIds: courseIds },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async addTestPaperTemplateToTestPaper(
    testPaperInputs: TestPaperTemplateInputs,
  ): Promise<ApiResponse> {
    try {
      // AR-15.1: the doc's TestPaperTemplate wire schema accepts only these 11 fields; strip the
      // UI-only ActivityTemplate fields (MongoId, ActivityTypeId, TemplateName, AffiliationId,
      // IsMultiSubject, MinSubjects, MaxSubjects, SubjectCountString, DisplayRank, ActivityTagId)
      // at the save boundary before sending.
      const template = testPaperInputs.TestPaperTemplate;
      const wireBody: TestPaperTemplateInputsWire = {
        MongoId: testPaperInputs.MongoId,
        TestPaperId: testPaperInputs.TestPaperId,
        TestPaperTemplate: {
          CourseId: template.CourseId,
          CourseName: template.CourseName,
          Duration: template.Duration,
          DifficultyLevelId: template.DifficultyLevelId,
          MarkingSchemeId: template.MarkingSchemeId,
          MaximumMarks: template.MaximumMarks,
          IsActive: template.IsActive,
          SubjectIds: template.SubjectIds,
          QuestionTypeConfigs: template.QuestionTypeConfigs,
          SectionConfigs: template.SectionConfigs,
          Sets: template.Sets,
        },
      };
      const response = await this.axiosInstance.put<boolean>(
        `test-series/add-test-paper-template-to-test-paper`,
        wireBody,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteTestPaperTemplate(
    mongoId: string,
    testPaperId: string,
    courseId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `test-series/delete-test-paper-template`,
        { params: { mongoId: mongoId, testPaperId: testPaperId, courseId: courseId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteTestPaper(mongoId: string, testPaperId: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(`test-series/delete-test-paper`, {
        params: { mongoId: mongoId, testPaperId: testPaperId },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateTestSeriesSet(
    updateTestSeriesSetInputs: UpdateTestSeriesSetInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `test-series/update-test-series-set`,
        updateTestSeriesSetInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteTestSeriesSet(
    affiliationId: number,
    mongoId: string,
    testPaperId: string,
    courseId: number,
    activityId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `test-series/delete-test-series-set`,
        {
          params: {
            affiliationId: affiliationId,
            mongoId: mongoId,
            testPaperId: testPaperId,
            courseId: courseId,
            activityId: activityId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTestSeriesForBatch(mongoId: string, batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<TestSeriesBatch>(
        `test-series/get-test-series-for-batch`,
        { params: { mongoId: mongoId, batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async publishTestPaperForBatch(
    mongoId: string,
    instituteTestId: number,
    batchIds: Array<number>,
    startDatetime: string,
    endDatetime: string,
    onlyOffline: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `test-series/publish-test-paper-for-batch`,
        {
          MongoId: mongoId,
          InstituteTestId: instituteTestId,
          BatchIds: batchIds,
          StartDatetime: startDatetime,
          EndDatetime: endDatetime,
          OnlyOffline: onlyOffline,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTestSeriesSchoolTestReport(
    affiliationId: number,
    batchId: number,
    instituteTestIds: Array<number>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<PdfReportDisplay>(
        `test-series/get-test-series-school-test-report`,
        { AffiliationId: affiliationId, InstituteTestIds: instituteTestIds, BatchId: batchId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createTestSeriesActivity(newActivity: TestPaperActivityInputs): Promise<ApiResponse> {
    try {
      // AR-7.3: strip each section's Questions to the wire rows at the save boundary, matching
      // create-activity / save-activity-questions.
      const wireBody = {
        ...newActivity,
        Sections: renumberQuestionSerialsAcrossSections(
          newActivity.Sections.map(toActivitySectionInput),
        ),
      };
      const response = await this.axiosInstance.post<number>(
        `test-series/create-test-series-activity`,
        wireBody,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
